import {Component, DestroyRef, EventEmitter, forwardRef, Inject, Input, InjectionToken, OnChanges, OnInit, Optional, Output, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MetadataType, MetadataTypeId} from '@app/graphql-typings/graphql';
import {TemplateCategoryOption} from '@app/layout/rules/template-editor/api/types';
import {TemplateCategoriesService} from '@app/layout/rules/template-categories/template-categories.service';
import {TemplateCategory, TemplateCategoryId, TemplateCategorySearchTerm} from '@app/layout/rules/template-categories/models/template-category';
import {CacheByParams} from '@app/shared/data-stores/cache-by-params';
import {I18nMessage} from '@shared/api/model/i18n';
import {ArrayUtils, CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import {MetadataTypeService} from '@shared/services/api/metadata-type.service';
import {finalize, forkJoin, map, Observable, of, Subscription} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AceLogger} from '@app/logging/ace-logger';
import {AceLoggerService} from '@app/logging/ace-logger.service';

export type TemplateCategorySelectSearchTerm = Pick<TemplateCategorySearchTerm, 'template_category_ids' | 'metadata_type_ids' | 'fault_mode_ids' | 'component_ids' | 'shared'>;
export type TemplateCategorySelectValue = TemplateCategoryId | TemplateCategoryId[] | TemplateCategoryOption | TemplateCategoryOption[] | null;

export const TEMPLATE_CATEGORIES_CACHE_TOKEN = new InjectionToken<CacheByParams<[TemplateCategorySelectSearchTerm | null], TemplateCategory[]>>('TemplateCategorySelectComponent.templateCategoriesCache');
export const METADATA_TYPES_CACHE_TOKEN = new InjectionToken<CacheByParams<[], Map<MetadataTypeId, MetadataType>>>('TemplateCategorySelectComponent.metadataTypesCache');

@Component({
  selector: 'app-template-category-select',
  templateUrl: './template-category-select.component.html',
  styleUrls: ['./template-category-select.component.scss'],
  standalone: false,
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TemplateCategorySelectComponent), multi: true}]
})
export class TemplateCategorySelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  public static readonly DO_NOT_SEARCH_CRITERIA = {};
  private readonly logger: AceLogger;
  private searchTermSubscription: Subscription | null = null;
  private templateCategoriesCache: CacheByParams<[TemplateCategorySelectSearchTerm | null], TemplateCategory[]>;
  private metadataTypesCache: CacheByParams<[], Map<MetadataTypeId, MetadataType>>;

  @Input() optionsSearchTerm: TemplateCategorySelectSearchTerm | null = null;
  @Input() placeholder: I18nMessage = {messageKey: 'Select Template Category'};
  @Input() appendTo: string;
  @Input() isReadOnly = false;
  @Input() bindValueToId = true;
  @Input() multiSelect = false;
  @Input() maxSelectedLabels = 3;
  @Input() truncateLabels = false;
  @Input() maxSelectedShowItemName = true;
  @Input() maxSelectedItemName: I18nMessage = {messageKey: 'categories'};
  @Input() hideSelected = false;
  @Output() dataLoaded = new EventEmitter<TemplateCategoryOption[]>();
  @Output() openEvent = new EventEmitter<void>();
  @Output() closeEvent = new EventEmitter<void>();

  protected readonly control = new FormControl<TemplateCategorySelectValue>({value: null, disabled: true});
  protected loading = false;
  protected categories: TemplateCategoryOption[] | null = null;

  private onChange: (v: TemplateCategorySelectValue) => void = () => {};
  protected onTouched: () => void = () => {};

  static buildTemplateCategoriesCache(service: TemplateCategoriesService,
                                      templateCategories: TemplateCategory[] | null = null): CacheByParams<[TemplateCategorySelectSearchTerm | null], TemplateCategory[]> {
    return new CacheByParams<[TemplateCategorySelectSearchTerm | null], TemplateCategory[]>(
      (searchTerm: TemplateCategorySelectSearchTerm | null) => searchTerm == null && templateCategories
        ? of(templateCategories)
        : service.getAllTemplateCategories(searchTerm),
      (searchTerm: TemplateCategorySelectSearchTerm | null) => TemplateCategorySelectComponent.makeTemplateCategoriesCacheKey(searchTerm)
    );
  }

  static buildMetadataTypesCache(service: MetadataTypeService,
                                 metadataTypes: MetadataType[] | null = null): CacheByParams<[], Map<MetadataTypeId, MetadataType>> {
    return new CacheByParams<[], Map<MetadataTypeId, MetadataType>>(
      () => (metadataTypes ? of(metadataTypes) : service.getAllMetadataTypes({commissionable: null})).pipe(
        map(allMetadataTypes => CollectionUtils.indexBy(allMetadataTypes, 'type_id'))
      ),
      () => ''
    );
  }

  private static makeTemplateCategoriesCacheKey(term: TemplateCategorySelectSearchTerm | null): string {
    if (term != null) {
      const templateCategoryIds = term.template_category_ids?.slice().sort().join(',') ?? null;
      const metadataTypeIds = term.metadata_type_ids?.slice().sort().join(',') ?? null;
      const faultModeIds = term.fault_mode_ids?.slice().sort().join(',') ?? null;
      const componentIds = term.component_ids?.slice().sort().join(',') ?? null;
      return `template_category_ids: ${templateCategoryIds}, metadata_type_ids: ${metadataTypeIds}, `
        + `fault_mode_ids: ${faultModeIds}, component_ids: ${componentIds}, shared: ${term.shared ?? null}`;
    } else {
      return '';
    }
  }

  constructor(
    private readonly templateCategoriesService: TemplateCategoriesService,
    private readonly metadataTypeService: MetadataTypeService,
    private readonly destroyRef: DestroyRef,
    @Optional() @Inject(TEMPLATE_CATEGORIES_CACHE_TOKEN) templateCategoriesCache: CacheByParams<[TemplateCategorySelectSearchTerm | null], TemplateCategory[]> | null,
    @Optional() @Inject(METADATA_TYPES_CACHE_TOKEN) metadataTypesCache: CacheByParams<[], Map<MetadataTypeId, MetadataType>> | null,
    logService: AceLoggerService
  ) {
    this.logger = logService.create(TemplateCategorySelectComponent.name);
    this.checkAndInitializeTemplateCategoriesCache(templateCategoriesCache);
    this.checkAndInitializeMetadataTypesCache(metadataTypesCache);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.optionsSearchTerm) {
      const prev: TemplateCategorySelectSearchTerm | null = changes.optionsSearchTerm.previousValue ?? null;
      if (!this.searchTermsEqual(prev, this.optionsSearchTerm)) {
        if (this.shouldPerformSearch(this.optionsSearchTerm)) {
          this.onSearchTermChanged(this.optionsSearchTerm);
        } else {
          this.searchTermSubscription?.unsubscribe();
          this.control.setValue(null, {emitEvent: false});
          this.categories = null;
        }
      }
    }
  }

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => this.onChange(v));
    if (!this.searchTermSubscription && this.shouldPerformSearch(this.optionsSearchTerm)) {
      this.onSearchTermChanged(this.optionsSearchTerm);
    }
  }

  writeValue(value: TemplateCategorySelectValue): void {
    this.control.setValue(value, {emitEvent: false});
  }

  registerOnChange(fn: (v: TemplateCategorySelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      if (this.control.enabled) {
        this.control.disable({emitEvent: false});
      }
    } else {
      if (this.control.disabled) {
        this.control.enable({emitEvent: false});
      }
    }
  }

  protected onOpen(): void {
    this.openEvent.emit();
  }

  protected onClose(): void {
    this.closeEvent.emit();
  }

  private searchTermsEqual(a: TemplateCategorySelectSearchTerm | null, b: TemplateCategorySelectSearchTerm | null): boolean {
    if (a == null || b == null) {
      return a == b;
    } else {
      return ArrayUtils.containSameItems(a.template_category_ids ?? null, b.template_category_ids ?? null)
        && ArrayUtils.containSameItems(a.metadata_type_ids ?? null, b.metadata_type_ids ?? null)
        && ArrayUtils.containSameItems(a.fault_mode_ids ?? null, b.fault_mode_ids ?? null)
        && ArrayUtils.containSameItems(a.component_ids ?? null, b.component_ids ?? null)
        && ((a.shared ?? null) === (b.shared ?? null));
    }
  }

  private shouldPerformSearch(term: TemplateCategorySelectSearchTerm | null): boolean {
    return term == null || !ObjectUtils.isEmpty(term);
  }

  private getTemplateCategories(searchTerm: TemplateCategorySelectSearchTerm | null): Observable<TemplateCategory[]> {
    return this.templateCategoriesCache.hasCachedItem(null)
      // if all template categories are already fetched, filter locally, otherwise fallback to remote fetching
      ? this.templateCategoriesCache.get(null).pipe(map(templateCategories =>
          this.filterTemplateCategories(templateCategories, searchTerm)))
      : this.templateCategoriesCache.get(searchTerm);
  }

  private filterTemplateCategories(templateCategories: TemplateCategory[] | null, searchTerm: TemplateCategorySelectSearchTerm | null): TemplateCategory[] {
    if (searchTerm != null) {
      const templateCategoryIds = searchTerm.template_category_ids ? new Set(searchTerm.template_category_ids) : null;
      const metadataTypeIds = searchTerm.metadata_type_ids ? new Set(searchTerm.metadata_type_ids) : null;
      const faultModeIds = searchTerm.fault_mode_ids ? new Set(searchTerm.fault_mode_ids) : null;
      const componentIds = searchTerm.component_ids ? new Set(searchTerm.component_ids) : null;
      const shared = searchTerm.shared ?? null;
      return templateCategories?.filter(category => {
        return (templateCategoryIds?.has(category.template_category_id) ?? true)
          && (metadataTypeIds?.has(category.metadata_type_id) ?? true)
          && (faultModeIds?.has(category.fault_mode_id) ?? true)
          && (
            componentIds
              ? (componentIds.size === 0 ? category.component_id == null : componentIds.has(category.component_id))
              : true
          )
          && (shared == null || category.shared === shared);
      }) ?? [];
    } else {
      return templateCategories ?? [];
    }
  }

  private onSearchTermChanged(searchTerm: TemplateCategorySelectSearchTerm | null): void {
    this.loading = true;
    this.searchTermSubscription?.unsubscribe();
    this.searchTermSubscription = forkJoin([
      this.metadataTypesCache.get(),
      this.getTemplateCategories(searchTerm)
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.searchTermSubscription = null;
        this.loading = false;
      })
    ).subscribe(([metadataTypesById, categories]) => {
      const options = this.buildAndSortOptions(categories, metadataTypesById);
      if (options.length === 0) {
        this.categories = null;
        this.control.setValue(null, {emitEvent: false});
        this.onChange(null);
      } else {
        this.categories = options;
        const currentValue = this.control.value;
        this.filterSelectedValue(currentValue, options);
      }
      this.dataLoaded.emit(options);
    });
  }

  private filterSelectedValue(currentValue: TemplateCategorySelectValue, options: TemplateCategoryOption[]): void {
    const currentIds = this.extractIds(currentValue);
    if (currentIds?.length) {
      const optionsById = CollectionUtils.indexBy(options, 'template_category_id');
      const filteredIds = currentIds.filter(id => optionsById.has(id));
      if (!ArrayUtils.containSameItems(currentIds, filteredIds)) {
        const newValue = filteredIds.length
          ? this.buildValue(filteredIds, optionsById)
          : null;
        this.control.setValue(newValue, {emitEvent: false});
        this.onChange(newValue);
      }
    }
  }

  private buildValue(filteredIds: TemplateCategoryId[], optionsById: Map<TemplateCategoryId, TemplateCategoryOption>): TemplateCategorySelectValue {
    if (this.bindValueToId) {
      return this.multiSelect ? filteredIds : (filteredIds?.[0] ?? null);
    } else {
      const matchingOptions = ArrayUtils.keep(filteredIds, id => optionsById.get(id));
      return this.multiSelect ? matchingOptions : (matchingOptions?.[0] ?? null);
    }
  }

  private extractIds(value: TemplateCategorySelectValue): TemplateCategoryId[] | null {
    if (value != null) {
      const arr = Array.isArray(value) ? value : [value];
      return this.bindValueToId
        ? <TemplateCategoryId[]>arr
        : (<TemplateCategoryOption[]>arr).map(o => o.template_category_id);
    } else {
      return null;
    }
  }

  private buildAndSortOptions(categories: TemplateCategory[] | null, metadataTypesById: Map<MetadataTypeId, MetadataType>): TemplateCategoryOption[] {
    return categories?.map(category => {
      const metadataType = metadataTypesById.get(category.metadata_type_id);
      return {
        ...category,
        metadataType,
        displayName: this.templateCategoriesService.makeCategoryDisplayName(
          metadataType?.type_code, category.fault_mode?.name, category.component?.name ?? null
        )
      };
    })?.sort((c1, c2) => c1.displayName.localeCompare(c2.displayName)) ?? [];
  }

  private checkAndInitializeTemplateCategoriesCache(cache: CacheByParams<[TemplateCategorySelectSearchTerm | null], TemplateCategory[]> | null): void {
    if (cache != null) {
      this.templateCategoriesCache = cache;
    } else if (this.templateCategoriesCache == null) {
      this.logger.debug('templateCategoriesCache is not provided, initializing...');
      this.templateCategoriesCache = TemplateCategorySelectComponent.buildTemplateCategoriesCache(this.templateCategoriesService);
    }
  }

  private checkAndInitializeMetadataTypesCache(cache: CacheByParams<[], Map<MetadataTypeId, MetadataType>> | null): void {
    if (cache != null) {
      this.metadataTypesCache = cache;
    } else if (this.metadataTypesCache == null) {
      this.logger.debug('metadataTypesCache is not provided, initializing...');
      this.metadataTypesCache = TemplateCategorySelectComponent.buildMetadataTypesCache(this.metadataTypeService);
    }
  }
}
